import axios from "axios";
import { apiUrl } from "../config/environment";
import { getAuthHeaders, getToken } from "../utils/authUtils";

export type Currency = "USD" | "EUR" | "GBP";

export type InventoryItem = {
  _id: string;
  manufacturer: string;
  partNumber?: string;
  model?: string;
  title?: string;
  quantity: number;
  condition: string;
  price: number | null;
  currency: Currency;
  convertedPrice?: number;
  displayCurrency?: Currency;
  category?: string;
  description?: string;
  country?: string;
  isActive?: boolean;
  updatedAt?: string;
  sellerId?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
  };
};

export type InventoryListResponse = {
  total: number;
  page: number;
  limit: number;
  pages: number;
  items: InventoryItem[];
};

export type InventoryFilters = {
  userId: string;
  page?: number;
  limit?: number;
  q?: string;
  search?: string;
  category?: string;
  condition?: string;
  manufacturer?: string;
  country?: string;
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  isActive?: boolean;
};

export type BulkUploadResponse = {
  message: string;
  insertedCount: number;
  totalRows: number;
  successRate: string;
  errors?: string[];
};

// List inventory items by user ID with filters
export const listByUser = (params: InventoryFilters) => {
  const { userId, ...queryParams } = params;
  return axios.get<InventoryListResponse>(`${apiUrl}/api/inventory/admin/by-user/${userId}`, {
    params: queryParams,
    ...getAuthHeaders(),
  });
};

// Get single inventory item
export const getItem = (id: string) => {
  return axios.get<InventoryItem>(`${apiUrl}/api/inventory/${id}`, getAuthHeaders());
};

// Update inventory item
export const updateItem = (id: string, data: Partial<InventoryItem>) => {
  return axios.put<InventoryItem>(`${apiUrl}/api/inventory/${id}`, data, getAuthHeaders());
};

// Soft delete inventory item
export const softDelete = (id: string) => {
  return axios.delete<{ message: string }>(`${apiUrl}/api/inventory/${id}`, getAuthHeaders());
};

// Bulk upload CSV/XLSX file
export const bulkUpload = (file: File, preferredCurrency?: Currency) => {
  const formData = new FormData();
  formData.append("file", file);
  if (preferredCurrency) {
    formData.append("preferredCurrency", preferredCurrency);
  }
  // [2026-07-08] cookie-auth-fix: cookie (withCredentials) authenticates; no localStorage token required
  const token = getToken();
  return axios.post<BulkUploadResponse>(`${apiUrl}/api/inventory/upload`, formData, {
    headers: {
      Authorization: token || "",
      "Content-Type": "multipart/form-data",
    },
  });
};
// [2026-07-08] redeploy: re-commit cookie-auth fix to trigger rebuild
